import { useState, useEffect, useMemo, useCallback } from 'react'
import { ResponsiveBar } from '@nivo/bar'
import s from './PipelineByStage.scss'
import {
  makeReportsPostRequest,
  getReportsBaseUrl,
  makeGetRequest,
  makePatchRequest
} from 'utils/api'
import { toBarChartData, toUsers, getColors, toStages } from './serializers'
import { useLoading } from 'hooks'
import { getCurrency, getToken } from 'utils/authService'
import MaterialTable from '@material-table/core'
import { NumericFormat } from 'react-number-format'
import { RangeDatePicker, DatePicker } from 'components/ideal-core-ui'
import { utcDate, timezoneDate, currencyFormatter } from 'utils/helpers'
import isEmpty from 'lodash/isEmpty'
import { FaFileExcel, FaAngleRight, FaAngleDown, FaInfoCircle } from 'react-icons/fa'
import Cookies from 'universal-cookie'
import { COOKIES } from 'utils/constants'
import moment from 'moment'
import { AddButton } from 'components/common/AddButton'
import { Card, CardBody, CardHeader, Collapse, UncontrolledTooltip, Col, Row } from 'reactstrap'
import currency from 'currency.js'
import Select from 'react-select'
import { toTeams } from 'utils/serializers'
import { ExportCsv, ExportPdf } from '@material-table/exporters'

const columnStyle = {
  cellStyle: {
    fontWeight: 'bold',
    color: '#008c8c'
  },
  headerStyle: {
    fontWeight: 'bold',
    color: '#008c8c'
  }
}

const cookies = new Cookies()

const PipelineByStage = () => {
  const [pipelineInfo, setPipelineInfo] = useState(null)
  const [result, setResult] = useState([])
  const [users, setUsers] = useState([])
  const [userFilter, setUserFilter] = useState([])
  const [teamFilter, setTeamFilter] = useState([])
  const [stagesFilter, setStagesFilter] = useState([])
  const [teams, setTeams] = useState([])
  const [stages, setStages] = useState([])
  const [, withLoadingLocks] = useLoading()
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [collapse, setCollapse] = useState('')
  const [collapsedCategory, setCollapsedCategory] = useState('')

  const setDateFilters = (dates, utc = true) => {
    setStartDate(utc ? utcDate(dates.from) : dates.from)
    setEndDate(utc ? utcDate(dates.to) : dates.to)
  }

  useEffect(() => {
    const sessionDateRangeFilters = cookies.get(COOKIES.DATE_RANGE_FILTERS)
    if (sessionDateRangeFilters?.startDate) {
      setDateFilters(
        {
          from: sessionDateRangeFilters.startDate,
          to: sessionDateRangeFilters.endDate
        },
        false
      )
    } else {
      setDateFilters({
        from: moment().subtract(2, 'weeks').startOf('day').format(),
        to: moment().add(6, 'weeks').endOf('day').format()
      })
    }
  }, [])

  useEffect(() => {
    ;(async () => {
      const { content } = await makeGetRequest('/v1/users')
      setUsers(toUsers(content))

      const teams = await makeGetRequest('/v1/teams')
      setTeams(toTeams(teams))

      const stages = await makeGetRequest('/v1/dealstatus')
      setStages(toStages(stages))
    })()
  }, [])

  const refreshReport = useCallback(async () => {
    const result = await withLoadingLocks(
      makeReportsPostRequest('/v1/deals/stage', {
        from: startDate,
        to: endDate,
        ...(userFilter && userFilter.length
          ? { userFilter: userFilter.map(user => user.value) }
          : {}),
        ...(teamFilter && teamFilter.length
          ? { teamFilter: teamFilter.map(user => user.value) }
          : {}),
        ...(stagesFilter && stagesFilter.length
          ? { excludedStages: stagesFilter.map(stage => stage.value) }
          : {})
      })
    )
    setResult(result)
    setPipelineInfo(toBarChartData(result))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, userFilter, teamFilter, stagesFilter])

  useEffect(() => {
    ;(async () => {
      if (!startDate || !endDate) return
      refreshReport()
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, userFilter, teamFilter, stagesFilter])

  const onBtnExport = () => {
    window.open(
      getReportsBaseUrl(
        `/v1/deals/stage/export?from=${startDate}&to=${endDate}&userFilter=${userFilter
          .map(user => user.value)
          .join()}&teamFilter=${teamFilter
          .map(user => user.value)
          .join()}&excludedStages=${stagesFilter
          .map(stage => stage.value)
          .join()}&token=${getToken()}`
      ),
      '_blank'
    )
  }

  const toggleCollapse = (stage, category) => {
    if (collapse !== stage) {
      setCollapse(stage)
      setCollapsedCategory(category)
    } else {
      setCollapse('')
      setCollapsedCategory('')
    }
  }

  const columns = useMemo(() => {
    return [
      {
        title: 'Bid Due Date',
        field: 'bidDueDate',
        sorting: true,
        ...(['new', 'estimating', 'closed', 'lead'].includes(collapsedCategory)
          ? { ...columnStyle, defaultSort: 'asc' }
          : {}),
        width: '200px',
        editable: 'never',
        render: row => timezoneDate(row.bidDueDate),
        exportTransformer: row => timezoneDate(row.bidDueDate)
      },
      {
        title: 'Bid Follow-up Date',
        field: 'bidFollowUpDate',
        ...(['followup', 'project'].includes(collapsedCategory)
          ? { ...columnStyle, defaultSort: 'asc' }
          : {}),
        sorting: true,
        editable: 'never',
        width: '200px',
        render: row => (row.bidFollowUpDate ? timezoneDate(row.bidFollowUpDate) : null),
        exportTransformer: row => (row.bidFollowUpDate ? timezoneDate(row.bidFollowUpDate) : null)
      },
      {
        title: 'Submission Date',
        field: 'bidSubmissionDate',
        sorting: true,
        width: '200px',
        editable: 'never',
        render: row => row.bidSubmissionDate && timezoneDate(row.bidSubmissionDate),
        exportTransformer: row => row.bidSubmissionDate && timezoneDate(row.bidSubmissionDate)
      },
      {
        title: 'Created Date',
        field: 'createdDate',
        sorting: true,
        width: '200px',
        editable: 'never',
        render: row => row.createdDate && timezoneDate(row.createdDate),
        exportTransformer: row => row.createdDate && timezoneDate(row.createdDate)
      },
      {
        title: 'Last Updated',
        field: 'updatedDate',
        sorting: true,
        width: '200px',
        editable: 'never',
        render: row => row.updatedDate && timezoneDate(row.updatedDate),
        exportTransformer: row => row.updatedDate && timezoneDate(row.updatedDate)
      },
      {
        title: 'Won Date',
        field: 'wonOnDate',
        ...(collapsedCategory === 'won' ? { ...columnStyle, defaultSort: 'asc' } : {}),
        sorting: true,
        width: '200px',
        editable: 'never',
        render: row => row.wonOnDate && timezoneDate(row.wonOnDate),
        exportTransformer: row => row.wonOnDate && timezoneDate(row.wonOnDate)
      },
      {
        title: 'Lost Date',
        field: 'lostOnDate',
        ...(collapsedCategory === 'lost' ? { ...columnStyle, defaultSort: 'asc' } : {}),
        sorting: true,
        width: '200px',
        editable: 'never',
        render: row => row.lostOnDate && timezoneDate(row.lostOnDate),
        exportTransformer: row => row.lostOnDate && timezoneDate(row.lostOnDate)
      },
      {
        title: 'Expected Close Date',
        field: 'expectedCloseDate',
        sorting: true,
        width: '200px',
        render: row => (row.expectedCloseDate ? timezoneDate(row.expectedCloseDate) : null),
        exportTransformer: row =>
          row.expectedCloseDate ? timezoneDate(row.expectedCloseDate) : null,
        emptyValue: '(empty)',
        editComponent: props => {
          return (
            <DatePicker
              keyProp={props.rowData.dealId}
              value={
                props.rowData.expectedCloseDate && timezoneDate(props.rowData.expectedCloseDate)
              }
              onChange={async value => {
                const date = value && value !== null ? utcDate(value) : ''
                await makePatchRequest(`/v1/deals/${props.rowData.id}`, {
                  expectedCloseDate: date
                })
                refreshReport()
              }}
              placeholder="Select Expected Close Date"
            />
          )
        }
      },
      {
        title: '% in Forecast',
        field: 'closingChance',
        sorting: true,
        width: '200px',
        render: row => `${row.closingChance}%`,
        exportTransformer: row => `${row.closingChance}%`
      },
      {
        title: 'Quote #',
        field: 'quoteNumber',
        sorting: true,
        width: '200px',
        editable: 'never'
      },
      {
        title: 'Project #',
        field: 'projectNumber',
        width: '200px',
        editable: 'never',
        sorting: true
      },
      {
        title: 'Opp Status',
        field: 'status',
        width: '200px',
        editable: 'never',
        sorting: true
      },
      {
        title: 'Opp Name',
        field: 'name',
        width: '200px',
        sorting: true,
        editable: 'never',
        render: row => (
          <a href={`/opportunities/${row.id}`} target="_blank" rel="noreferrer">
            {row.name}
          </a>
        )
      },
      {
        title: 'Geographical Area',
        field: 'geographicalAreaName',
        width: '200px',
        editable: 'never',
        sorting: true
      },
      {
        title: 'Client(s)',
        field: 'companies',
        sorting: true,
        width: '200px',
        editable: 'never',
        render: row => row.companies.join(', '),
        exportTransformer: row => row.companies.join(', ')
      },
      {
        title: 'Contact(s)',
        field: 'contacts',
        sorting: true,
        width: '400px',
        editable: 'never',
        render: row => {
          return row.contacts
            .map(
              c =>
                `${c.company}, ${c.name}, ${c.phone ? c.phone + ', ' : ''}${
                  c.cellPhone ? c.cellPhone + ', ' : ''
                }${c.email}`
            )
            .join(' | ')
        },
        exportTransformer: row => {
          return row.contacts
            .map(
              c =>
                `${c.company}, ${c.name}, ${c.phone ? c.phone + ', ' : ''}${
                  c.cellPhone ? c.cellPhone + ', ' : ''
                }${c.email}`
            )
            .join(' | ')
        }
      },
      {
        title: 'Awarded To',
        field: 'awardedCompany',
        width: '200px',
        editable: 'never',
        sorting: true
      },
      {
        title: 'Won Value',
        field: 'wonValue',
        width: '200px',
        editable: 'never',
        sorting: true,
        render: row => (
          <NumericFormat
            thousandSeparator
            decimalScale={2}
            value={row.wonValue}
            prefix={getCurrency().symbol}
            displayType="text"
          />
        ),
        exportTransformer: row => currencyFormatter({ value: row.wonValue })
      },
      {
        title: 'Value',
        field: 'value',
        width: '200px',
        editable: 'never',
        sorting: true,
        render: row => (
          <NumericFormat
            thousandSeparator
            decimalScale={2}
            value={row.value}
            prefix={getCurrency().symbol}
            displayType="text"
          />
        ),
        exportTransformer: row => currencyFormatter({ value: row.value })
      },
      {
        title: 'Work type',
        field: 'dealWorkTypeName',
        sorting: true,
        width: '200px',
        editable: 'never'
      },
      {
        title: '% of Stage Value',
        field: 'percentOfStageValue',
        width: '200px',
        sorting: true,
        editable: 'never',
        render: row => row.percentOfStageValue + '%',
        exportTransformer: row => row.percentOfStageValue + '%'
      },
      {
        title: 'Cost',
        field: 'cost',
        width: '200px',
        editable: 'never',
        sorting: true,
        render: row => (
          <NumericFormat
            thousandSeparator
            decimalScale={2}
            value={row.cost}
            prefix={getCurrency().symbol}
            displayType="text"
          />
        ),
        exportTransformer: row => currencyFormatter({ value: row.cost })
      },
      {
        title: 'Profit',
        field: 'grossProfit',
        width: '200px',
        editable: 'never',
        sorting: true,
        render: row => (
          <NumericFormat
            thousandSeparator
            decimalScale={2}
            value={row.grossProfit}
            prefix={getCurrency().symbol}
            displayType="text"
          />
        ),
        exportTransformer: row => currencyFormatter({ value: row.grossProfit })
      },
      {
        title: 'Team(s)',
        field: 'teams',
        width: '250px',
        editable: 'never',
        sorting: true,
        render: row => row.teams?.join(', '),
        exportTransformer: row => row.teams?.join(', ')
      },
      {
        title: 'Opp Manager',
        field: 'manager',
        width: '200px',
        editable: 'never',
        sorting: true
      }
    ]
  }, [collapsedCategory, refreshReport])

  return (
    <>
      <h6 className={s.title}>Pipeline Reports &gt; Pipeline by Stage</h6>
      <div className={s.filters}>
        <RangeDatePicker startDate={startDate} endDate={endDate} onChange={setDateFilters} />
        <Select
          onChange={values => setUserFilter(values)}
          options={users}
          value={userFilter}
          placeholder="Select User(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type1" style={{ position: 'relative', top: 15, left: 5 }} />
        <UncontrolledTooltip placement="bottom" target="Type1">
          To see all opportunities, please remove all users from selection.
        </UncontrolledTooltip>
        <Select
          onChange={values => setTeamFilter(values)}
          options={teams}
          value={teamFilter}
          placeholder="Select Team(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type2" style={{ position: 'relative', top: 15, left: 5 }} />
        <UncontrolledTooltip placement="bottom" target="Type2">
          To see all opportunities, please remove all teams from selection.
        </UncontrolledTooltip>
        <Select
          onChange={values => setStagesFilter(values)}
          options={stages}
          value={stagesFilter}
          placeholder="Exclude Stage from Report"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type3" style={{ position: 'relative', top: 15, left: 5 }} />
        <UncontrolledTooltip placement="bottom" target="Type3">
          Select Stages you&#39;d like to exclude from report.
        </UncontrolledTooltip>
      </div>
      {Object.keys(result).length > 0 && !isEmpty(pipelineInfo) ? (
        <>
          <Row>
            <Col lg="1">
              <div className={s.info}>
                <p>Total Pipeline: </p>
                <p>
                  <NumericFormat
                    thousandSeparator
                    decimalScale={2}
                    value={pipelineInfo.totalValue}
                    prefix={getCurrency().symbol}
                    displayType="text"
                  />
                </p>
                <p>Opportunities: {pipelineInfo.totalCount}</p>
              </div>
            </Col>
            <Col lg="11">
              <div className={s.totalChart}>
                <ResponsiveBar
                  colors={bar => getColors(bar, stages)}
                  data={[pipelineInfo.stackedBarByValue]}
                  keys={pipelineInfo.keys}
                  margin={{ top: 0, right: 130, bottom: 50, left: 60 }}
                  padding={0.15}
                  layout="horizontal"
                  borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                  axisTop={null}
                  axisRight={null}
                  axisBottom={null}
                  axisLeft={null}
                  tooltip={({ id, value, data }) => (
                    <span>
                      {id}:{' '}
                      <strong>
                        {
                          <NumericFormat
                            thousandSeparator
                            decimalScale={2}
                            value={value}
                            prefix={data.id}
                            displayType="text"
                          />
                        }
                      </strong>
                    </span>
                  )}
                  enableGridY={false}
                  enableLabel={false}
                  labelSkipWidth={12}
                  labelSkipHeight={9}
                  labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                  animate
                  motionStiffness={90}
                  motionDamping={15}
                />
              </div>
            </Col>
          </Row>
          <div
            className={s.barChart}
            style={{ height: Object.keys(result).length * 60 + 'px', maxHeight: 1000 }}
          >
            <ResponsiveBar
              colors={bar => getColors(bar, stages)}
              data={pipelineInfo.barByValue}
              keys={pipelineInfo.keys}
              margin={{ top: 0, right: 200, bottom: 50, left: 170 }}
              padding={0.45}
              layout="horizontal"
              borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legendPosition: 'middle',
                legendOffset: 32
              }}
              axisLeft={{
                tickSize: 0,
                tickPadding: 5,
                tickRotation: 0,
                legendPosition: 'middle',
                legendOffset: -40
              }}
              tooltip={({ id, value, data }) => (
                <span>
                  {id}:{' '}
                  <strong>
                    {data.currency ? (
                      <NumericFormat
                        thousandSeparator
                        decimalScale={2}
                        value={value}
                        prefix={data.currency}
                        displayType="text"
                      />
                    ) : (
                      value
                    )}
                  </strong>
                </span>
              )}
              enableGridY
              enableGridX
              enableLabel={false}
              labelSkipWidth={12}
              labelSkipHeight={9}
              labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
              legends={[
                {
                  dataFrom: 'keys',
                  anchor: 'bottom-right',
                  direction: 'column',
                  justify: false,
                  translateX: 120,
                  translateY: 0,
                  itemsSpacing: 14,
                  itemWidth: 100,
                  itemHeight: 20,
                  itemDirection: 'left-to-right',
                  itemOpacity: 0.85,
                  symbolSize: 20,
                  effects: [
                    {
                      on: 'hover',
                      style: {
                        itemOpacity: 1
                      }
                    }
                  ]
                }
              ]}
              animate
              motionStiffness={90}
              motionDamping={15}
            />
          </div>
          <div className={s.table}>
            <AddButton className={s.exportExcel} onClick={onBtnExport}>
              <FaFileExcel className={s.excelIcon} />
              Export to Excel
            </AddButton>
            {Object.keys(result).map(stage => (
              <Card className={s.card} key={stage}>
                <CardHeader
                  onClick={() => toggleCollapse(stage, result[stage].statusCategoryCode)}
                  data-event={stage}
                >
                  <span className={s.arrow}>
                    {collapse === stage ? <FaAngleRight /> : <FaAngleDown />}
                  </span>
                  {stage} ({result[stage].count}) &#8226; Bid:{' '}
                  {currency(result[stage].totalValue, { symbol: getCurrency().symbol }).format()}{' '}
                  &#8226; % of Total Value: {result[stage].percentOfTotalValue + '%'} &#8226; Cost:{' '}
                  {currency(result[stage].totalCostValue, {
                    symbol: getCurrency().symbol
                  }).format()}{' '}
                  &#8226; Profit:{' '}
                  {currency(result[stage].totalProfitValue, {
                    symbol: getCurrency().symbol
                  }).format()}
                </CardHeader>
                <Collapse isOpen={collapse === stage}>
                  <CardBody>
                    {collapse === stage && (
                      <MaterialTable
                        title=""
                        key={stage}
                        columns={columns}
                        data={result[stage].deals}
                        options={{
                          tableLayout: 'fixed',
                          sorting: true,
                          headerStyle: {
                            backgroundColor: '#eee',
                            color: '#000',
                            fontSize: '14px'
                          },
                          pageSize: 10,
                          padding: 'dense',
                          pageSizeOptions: [
                            5,
                            10,
                            20,
                            50,
                            { value: result[stage].deals.length, label: 'All' }
                          ],
                          emptyRowsWhenPaging: false,
                          exportMenu: [
                            {
                              label: 'Export PDF',
                              exportFunc: (cols, datas) =>
                                ExportPdf(cols, datas, 'Pipeline by Stage')
                            },
                            {
                              label: 'Export CSV',
                              exportFunc: (cols, datas) =>
                                ExportCsv(cols, datas, 'Pipeline by Stage')
                            }
                          ],
                          columnsButton: true
                        }}
                        style={{
                          boxShadow: 'none',
                          fontSize: '13px'
                        }}
                        cellEditable={{
                          onCellEditApproved: async (newValue, oldValue, rowData) => {
                            if (rowData.tableData.editCellList[0].field === 'expectedCloseDate')
                              return new Promise(resolve => resolve())
                            await makePatchRequest(`/v1/deals/${rowData.id}`, {
                              closingChance: newValue
                            })
                            refreshReport()
                          }
                        }}
                      />
                    )}
                  </CardBody>
                </Collapse>
              </Card>
            ))}
          </div>
        </>
      ) : (
        <p className={s.noResults}>No results to display. Try a different time range.</p>
      )}
    </>
  )
}

export default PipelineByStage
